import useModal from '@area2k/use-modal';
import { useApolloClient } from '@apollo/client';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';

import {
  costOfSchedule,
  sortSchedules,
  useOrderActions,
  useOrderState,
} from '../../../context';
import {
  JobDraftState,
  Schedule,
  Step,
  selectionOptions,
  useJobDraftActions,
} from '../../context';
import BottomBar from '../BottomBar';
import Layout from '../Layout';
import { FirstOrderNotice } from '../ScheduleStep';
import { getTotalHoursInDecimal } from '../ScheduleStep/util';
import SummaryTable from '../SummaryTable';

import JobCardDetailsRevamped from './JobCardDetailsRevamped';
import Summary from './Summary';
import PostSubmitModal from './modals/PostSubmitModal';
import RemoveJobFromOrderModal from './modals/RemoveJobModal';
import { buildInvitePayload } from './utils/buildInvitePayload';
import { buildOrderPayload } from './utils/buildOrderPayload';
import { buildOrderValueJobsFromPayload } from './utils/buildOrderValueJobsFromPayload';

import { SubmitErrorAlert } from '@/components/Alerts';
import type { SubmitErrorType } from '@/components/Alerts/SubmitErrorAlert/types';
import Avatar from '@/components/Avatar';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Subheading, Title } from '@/components/Typography';
import { GAEvent } from '@/constants/gaevents';
import useAuth from '@/hooks/useAuth';
import useMediaQuery from '@/hooks/useMediaQuery';
import PaymentSection from '@/routes/Agency/Account/PaymentSection';
import { neutral } from '@/styles/colors';
import { AccountRateTypeEnum, JobTypeEnum, Scalars } from '@/types/graphql';
import useAnalytics from '@/util/analytics';
import { evictDashboardShiftWeeks } from '@/util/apollo/dashboardCache';
import { useOrderCreationTracking } from '../../useOrderCreationTracking';
import { computeOrderTotalValue } from '@/util/vexo';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { calculateTotalHours, formatDuration } from '@/util/datetime';
import { centsToCurrency } from '@/util/number';
import { isIOS, isWeb } from '@/util/platform';

const calculateTotalHoursByJobs = (jobs: JobDraftState[]) => {
  return jobs.reduce((acc, jobElement) => {
    const calculatedHours = calculateTotalHours({
      schedules: jobElement.schedules,
    });

    if (calculatedHours) {
      return acc + calculatedHours;
    }

    return acc;
  }, 0);
};

const calculateTotalEstimateByJobs = (jobs: JobDraftState[]) => {
  return jobs.reduce((acc, jobElement) => {
    return (
      acc +
      jobElement.schedules.reduce((sacc, sel) => sacc + costOfSchedule(sel), 0)
    );
  }, 0);
};

type Props = {
  setStep: (step: Step) => Scalars['Void'];
  onClose: () => Scalars['Void'];
};

// Response type for REST API
type CreateOrderResponse = {
  order: {
    id: string;
  };
  workers_hired?: string;
  // GRAVY-10803. graphy answers 201 with `partial_success` when the order was
  // placed but some of the in-wizard hires were refused — a worker missing a
  // badge the job requires, among the other `validate_hire` reasons. Already
  // in the payload; this flow just never read it, so a refused hire looked
  // exactly like a successful one.
  partial_success?: boolean;
  errors?: string[];
};

const ReviewStep = ({ setStep, onClose }: Props) => {
  const [submitError, setSubmitError] = useState<SubmitErrorType | null>(null);
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState<CreateOrderResponse | null>(null);

  const { billing, jobs, orderType } = useOrderState();
  const { updateJob } = useJobDraftActions();
  const { removeJob, replaceJob } = useOrderActions();
  const { resetState } = useJobDraftActions();

  const startTime = new Date().getTime();

  const { logEvent } = useAnalytics();
  const { trackStepViewed, trackSubmitted } = useOrderCreationTracking();
  const apolloClient = useApolloClient();
  const { currentAgency } = useAuth();

  useEffect(() => {
    trackStepViewed('summary');
  }, [trackStepViewed]);

  const showMarkupBasedPricing =
    billing?.account.rateType === AccountRateTypeEnum.MARKUP;

  const { clearJobs, setBilling } = useOrderActions();

  const invitePayload = orderData ? buildInvitePayload(jobs) : null;

  const [showPostModal, hidePostModal] = useModal(
    () => (
      <PostSubmitModal
        hideModal={() => {
          hidePostModal();
          clearJobs();
          setBilling(null);
        }}
        hireMessage={orderData?.workers_hired}
        invitePayload={invitePayload}
        order={orderData!.order}
        staffingErrors={orderData?.errors ?? []}
      />
    ),
    [orderData, invitePayload],
  );

  const handleAddNewSkill = () => {
    resetState();
    setStep(Step.SKILL);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setSubmitError(null);

    try {
      // Build the REST payload
      const payload = buildOrderPayload(billing!, orderType!, jobs);

      // Log badge event if certificates are present
      if (
        payload.jobs[0]?.certificates &&
        payload.jobs[0].certificates.length > 0
      ) {
        logEvent(GAEvent.BadgeAddedOnJobCreation);
      }

      // Make the REST API call
      const response = await axiosRubyClient.post<CreateOrderResponse>(
        '/api/admin/orders',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      setOrderData(response.data);

      // GRAVY-10310: same gap as Order V2 — this flow creates the order over
      // REST, so Dashboard V1's cache-first Apollo weekly query never learns
      // about it. V1 additionally exits via `navigate(ROUTES.home)`, which does
      // not run the sidebar/navbar Dashboard click handler that sets the Redux
      // refetch flag, so without this the admin lands on a stale grid.
      evictDashboardShiftWeeks(apolloClient.cache, currentAgency?.id);

      // The order is already created at this point. Isolate analytics in its
      // own try/catch so a throw in the value adapters (which run before
      // track()'s internal guard) can't trip the submit catch below and show a
      // false "order failed" toast — which would invite a duplicate re-submit.
      try {
        const totalShifts = (payload.jobs ?? []).reduce(
          (acc, j) => acc + (j.shifts?.length ?? 0),
          0,
        );
        const firstJobRateCents = payload.jobs[0]?.pay_rate ?? 0;
        trackSubmitted({
          order_id: response.data.order.id,
          shifts_required: totalShifts,
          pay_rate: firstJobRateCents > 0 ? firstJobRateCents / 100 : null,
          shift_type:
            jobs[0]?.postSetting === selectionOptions.MY_SELECTIONS
              ? 'invited'
              : 'public',
          // Parity with V2: region is the first job's location state.
          region: jobs[0]?.address?.state ?? null,
          total_value: computeOrderTotalValue(
            buildOrderValueJobsFromPayload(payload),
          ),
        });
      } catch {
        /* analytics must never fail a successful order */
      }
    } catch (error: any) {
      console.error('Failed to create order:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'An error occurred while creating the order';
      setSubmitError({ message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderData) {
      // Log analytics event
      if (billing?.account.rateType === AccountRateTypeEnum.MARKUP) {
        logEvent(
          orderType === JobTypeEnum.LTA
            ? GAEvent.StepSixMarkupLta
            : GAEvent.StepSixMarkupGig,
          billing?.account?.id,
          {
            time_spent: (new Date().getTime() - startTime) / 1000,
            platform: isWeb() ? 'web' : isIOS() ? 'ios' : 'android',
          },
        );
      } else {
        logEvent(
          orderType === JobTypeEnum.LTA
            ? GAEvent.StepFiveStaticLta
            : GAEvent.StepFiveStaticGig,
          orderData?.order?.id,
          {
            time_spent: (new Date().getTime() - startTime) / 1000,
            platform: isWeb() ? 'web' : isIOS() ? 'ios' : 'android',
          },
        );
      }
      showPostModal();
    }
  }, [orderData, showPostModal]);

  const totalHours = formatDuration(calculateTotalHoursByJobs(jobs));
  const totalDecimalHours = getTotalHoursInDecimal(
    calculateTotalHoursByJobs(jobs),
  );
  const totalEstimate = calculateTotalEstimateByJobs(jobs);
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const paddings = phoneOnly ? { paddingLeft: 0, paddingRight: 0 } : {};

  useEffect(() => {
    if (jobs.length === 0) onClose();
  }, [jobs]);

  const removeSkillJob = (index) => {
    removeJob(index);
  };

  const updateSchedule = (jobIndex, schedules: Schedule[]) => {
    const job = jobs[jobIndex];
    if (schedules.length) {
      job.schedules = sortSchedules(schedules);
      replaceJob(jobIndex, job);
    } else {
      removeJob(jobIndex);
    }
  };

  const [showRemoveJobModal, hideRemoveJobModal] = useModal(
    ({ index }: { index: number }) => (
      <RemoveJobFromOrderModal
        hideModal={hideRemoveJobModal}
        onConfirm={() => {
          removeSkillJob(index);
          hideRemoveJobModal();
        }}
      />
    ),
    [jobs],
  );
  const disableSubmit = jobs.some((job) => job.payRate! < job.minPay!);

  return (
    <Layout
      css={{
        backgroundColor: showMarkupBasedPricing
          ? 'white'
          : neutral.neutralGhost,
        ...paddings,
      }}
    >
      {submitError && <SubmitErrorAlert description={submitError.message} />}
      <Stack vertical align="center" gap={24}>
        <Grid container spacing={2.5}>
          <Grid item md={1.5} sm={0} />
          <Grid item lg={4.5} md={6} xs={12}>
            <Title
              css={{
                paddingBottom: 24,
                paddingLeft: phoneOnly ? 24 : 0,
              }}
            >{`Order for ${billing!.customer.name}`}</Title>
            <Stack vertical gap={32}>
              {jobs.map((job, index) => {
                return (
                  <>
                    {showMarkupBasedPricing ? (
                      <JobCardDetailsRevamped index={index} job={job} />
                    ) : (
                      <Card key={index} noRadius={phoneOnly}>
                        <Card.Section>
                          <Stack>
                            <Stack gap={8}>
                              <Avatar size="md" src={job.skill!.imageUrl!} />
                              <Subheading>{job.skill!.name}</Subheading>
                            </Stack>
                            <Stack
                              justify={'end'}
                              style={{ paddingRight: '10px' }}
                            >
                              <Card
                                actions={[
                                  {
                                    label: 'Remove',
                                    a11yLabel: 'Remove',
                                    onAction: () =>
                                      showRemoveJobModal({ index }),
                                  },
                                ]}
                                changeStyle={{ boxShadow: 'none' }}
                              />
                            </Stack>
                          </Stack>
                        </Card.Section>
                        <Card.Section>
                          <SummaryTable
                            handleJobsChange={(schedules: Schedule[]) => {
                              updateSchedule(index, schedules);
                            }}
                            postSetting={job.postSetting}
                            schedules={job.schedules}
                            skillId={job.skill?.id}
                            address={job.address}
                          />
                        </Card.Section>
                        <Summary billing={billing} job={job} />
                      </Card>
                    )}
                  </>
                );
              })}

              <Button
                a11yLabel="Add other skill to order"
                appearance="outline"
                css={{
                  width: phoneOnly ? 'calc(100% - 24px)' : '100%',
                  backgroundColor: '#EEFFEC',
                  margin: phoneOnly ? 'auto' : undefined,
                }}
                iconLeft={faPlus}
                id="add-skill-btn"
                label="Add Skills"
                type="button"
                onClick={handleAddNewSkill}
              />
            </Stack>
          </Grid>
          <Grid item lg={4} md={3} xs={12}>
            <div style={{ paddingTop: phoneOnly ? 16 : 60 }}>
              <Stack verticalGap={40}>
                <Card
                  headerNobg={true}
                  noRadius={phoneOnly}
                  showBorder={showMarkupBasedPricing ?? false}
                  title="Summary"
                >
                  <Card.Section>
                    <Stack vertical gap={16}>
                      <Stack justify="apart">
                        <Subheading>Total Hours</Subheading>
                        <Subheading>
                          {showMarkupBasedPricing
                            ? totalDecimalHours
                            : totalHours}
                        </Subheading>
                      </Stack>
                      <Stack justify="apart">
                        <Subheading>Total Estimate</Subheading>
                        <Subheading
                          color={
                            showMarkupBasedPricing ? 'themeDefault' : 'theme'
                          }
                        >
                          ${centsToCurrency(totalEstimate)}
                        </Subheading>
                      </Stack>
                    </Stack>
                  </Card.Section>
                </Card>
              </Stack>
              <Stack css={{ marginTop: 18 }} verticalGap={40}>
                <PaymentSection
                  hideChangeButton
                  account={billing!.account}
                  cardBorderOutline={showMarkupBasedPricing ?? false}
                  notice={<FirstOrderNotice billing={billing} />}
                  showMarkupBasedPricing={showMarkupBasedPricing}
                />
              </Stack>
            </div>
          </Grid>
          <Grid item md={1.5} sm={0} />
        </Grid>

        <BottomBar>
          <Button
            a11yLabel="Go back to previous step"
            appearance="outline"
            disabled={loading}
            id="back-btn"
            label="Back"
            type="button"
            onClick={() => {
              const lastjob = jobs[jobs.length - 1];
              if (lastjob) {
                updateJob({ ...lastjob });
              }
              removeJob(jobs.length - 1);
              if (billing?.account.rateType === AccountRateTypeEnum.MARKUP) {
                setStep(Step.PAYRATE);
              } else {
                setStep(Step.DETAILS);
              }
            }}
          />
          <Button
            a11yLabel="Submit Order"
            disabled={disableSubmit || loading}
            id="submit-order-btn"
            isLoading={loading}
            label="Submit order"
            type="button"
            onClick={handleSubmit}
          />
        </BottomBar>
      </Stack>
    </Layout>
  );
};

export default ReviewStep;
